import { type WorkspaceDoc } from '../rxdb/collections/workspaces';
import { useDatabase } from '../rxdb/provider';
import { useCallback, useEffect, useState } from 'react';
import { ulid } from 'ulid';

export const useWorkspaces = () => {
  const { db, userId } = useDatabase();
  const [workspaces, setWorkspaces] = useState<WorkspaceDoc[]>([]);

  useEffect(() => {
    const sub = db.workspaces
      .find({ sort: [{ position: 'asc' }] })
      .$.subscribe((docs) => {
        setWorkspaces(docs.map((doc) => doc.toJSON()));
      });
    return () => sub.unsubscribe();
  }, [db]);

  const create = useCallback(
    async (input: { title: string }) => {
      const now = new Date().toISOString();
      await db.workspaces.insert({
        createdAt: now,
        id: ulid(),
        position: workspaces.length,
        title: input.title,
        updatedAt: now,
        userId,
      });
    },
    [db, userId, workspaces.length],
  );

  const rename = useCallback(
    async (id: string, title: string) => {
      const doc = await db.workspaces.findOne(id).exec();
      if (!doc) {
        return;
      }

      if (doc.position === 0) {
        throw new Error('Cannot rename the Inbox workspace');
      }

      await doc.patch({ title, updatedAt: new Date().toISOString() });
    },
    [db],
  );

  const destroy = useCallback(
    async (input: {
      processDisposition: 'delete' | 'move';
      targetWorkspaceId?: string;
      workspaceId: string;
    }) => {
      const workspaceDoc = await db.workspaces
        .findOne(input.workspaceId)
        .exec();

      if (!workspaceDoc) {
        throw new Error('Workspace not found');
      }

      if (workspaceDoc.position === 0) {
        throw new Error('Cannot delete the Inbox workspace');
      }

      const processDocs = await db.processes
        .find({ selector: { workspaceId: input.workspaceId } })
        .exec();

      const now = new Date().toISOString();

      if (input.processDisposition === 'move') {
        const targetWorkspaceId =
          input.targetWorkspaceId ??
          workspaces.find((workspace) => workspace.position === 0)?.id;

        if (!targetWorkspaceId || targetWorkspaceId === input.workspaceId) {
          throw new Error('Choose a different workspace');
        }

        const targetWorkspace = await db.workspaces
          .findOne(targetWorkspaceId)
          .exec();

        if (!targetWorkspace) {
          throw new Error('Target workspace not found');
        }

        await Promise.all(
          processDocs.map((processDoc) =>
            processDoc.patch({
              updatedAt: now,
              workspaceId: targetWorkspaceId,
            }),
          ),
        );
      } else {
        await Promise.all(processDocs.map((processDoc) => processDoc.remove()));
      }

      await workspaceDoc.remove();
    },
    [db, workspaces],
  );

  return { create, destroy, rename, workspaces };
};
